import React, { useEffect, useMemo, useRef, useState } from "react";
import { renderError } from "../../../helpers/errors";
import { createFilterQueryString } from "../../../helpers/helpers";
import { createOptions, createProductOptions } from "../../../helpers/selects";
import api from "../../../services/api";
import InputSelect from "../../InputSelect";

export const SelectProduct = ({
    selectedProduct,
    setSelectedProduct,
    isMulti,
    changeProducts,
    selectedSubproductThumbnailUrl,
    onlyQuotable = false
}) => {
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState('');
    const [noOptionsText, setNoOptionsText] = useState('');

    const minSearch = 1;

    // Um AbortController por instância: `window.requestController` era global e
    // compartilhado com SearchProducts/SelectCompany/SelectCustomerCompany —
    // dois campos de busca na mesma tela cancelavam a requisição um do outro.
    const abortRef = useRef(null);

    // Espelho de `products` para leitura fora do ciclo de render: o debounce e
    // o callback da resposta rodam com o closure do render que os agendou, e
    // getSelectedProducts() precisa da lista atual para não perder de vista o
    // produto já selecionado. Mantido em handleSetProducts, único ponto que
    // escreve em `products`.
    const productsRef = useRef(products);

    // Debounce da digitação: a busca custa alguns segundos no servidor e antes
    // saía uma requisição por tecla (só para abortar a anterior na tecla
    // seguinte). "caneta" disparava seis buscas completas.
    useEffect(() => {
        if (search.length < minSearch) {
            searchProducts();
            return;
        }

        const timeout = setTimeout(() => searchProducts(), 500);
        return () => clearTimeout(timeout);
    }, [search]);

    // Requisição em voo não sobrevive à desmontagem do campo.
    useEffect(() => () => abortRef.current?.abort(), []);

    useEffect(() => {
        checkIfNeedToGet();
    }, [selectedProduct]);

    useEffect(() => {
        if(changeProducts){
            changeProducts(products);
        }
    }, [products]);

    const checkIfNeedToGet = () => {
        let productsIds = isMulti ? selectedProduct : [selectedProduct];
        let term = '';

        productsIds.map(productId => {
            if(productId === null) return;
            if(!products.some(product => product.id === productId)){
                term = productId;
            }
        });

        if(term){
            getProducts(term, true);
        } else {
            // Para forçar atualização da mensagem "Digite mais x caracteres"
            setSearch('');
        }
    }

    const clearOptions = () => {
        handleSetProducts([], true)
    }

    const searchProducts = () => {
        if(search.length < minSearch) {
            let diff = minSearch - search.length;
            setNoOptionsText(`Digite mais ${diff} ${diff === 1 ? 'caracter' : 'caracteres'}`);
            clearOptions();
            abortRef.current?.abort();
            abortRef.current = null;
            return;
        };

        getProducts(search);
    }

    // O produto já escolhido (busca pelo id) sempre aparece, mesmo que tenha
    // deixado de ser orçável depois: o filtro vale só para o que se digita.
    const getProducts = (term, isSelectedLookup = false) => {
        abortRef.current?.abort();

        const controller = new AbortController();
        abortRef.current = controller;

        // `fromClear` para esvaziar a lista SEM escrever "Nenhum resultado
        // encontrado": a mensagem certa enquanto a busca está em voo é
        // "Carregando...".
        handleSetProducts([], true);
        setNoOptionsText('Carregando...');

        api.get(`/product/search`, {
            // Em params, e não interpolado na URL: um termo com `&`, `#` ou
            // `+` quebrava a query string.
            params: onlyQuotable && !isSelectedLookup ? { term, quotable: 1 } : { term },
            signal: controller.signal
        }).then(res => {
            // Só a requisição corrente escreve na lista.
            if(abortRef.current !== controller) return;
            handleSetProducts(res.data.resources.data);
        }).catch(error => {
            if(error.code !== undefined){
                renderError(error);
            }
        });
    }

    const handleSetProducts = (resources, fromClear = false) => {
        let currentSelectedProducts = getSelectedProducts();

        let toProducts = resources.filter(resource => {
            return !currentSelectedProducts.some(current => current.id === resource.id);
        });

        const nextProducts = [...currentSelectedProducts, ...toProducts];

        productsRef.current = nextProducts;
        setProducts(nextProducts);
        if(!fromClear){
            setNoOptionsText(toProducts.length === 0 ? 'Nenhum resultado encontrado' : '');
        }
    }

    const getSelectedProducts = () => {
        return productsRef.current.filter(product => {
            if(isMulti){
                return selectedProduct.includes(product.id);
            } else {
                return selectedProduct === product.id;
            }
        });
    }

    const productOptions = useMemo(() => {
        const base = createProductOptions(products, 'name', 'id');
        if (selectedSubproductThumbnailUrl == null || selectedSubproductThumbnailUrl === '') {
            return base;
        }
        return base.map((opt) => {
            if (opt.value !== selectedProduct) return opt;
            return {
                ...opt,
                image: <img src={selectedSubproductThumbnailUrl} alt="" />
            };
        });
    }, [products, selectedProduct, selectedSubproductThumbnailUrl]);

    return (
        <InputSelect
            key={`prod-sel-${selectedProduct}-${selectedSubproductThumbnailUrl || 'def'}`}
            noOptionsText={noOptionsText}
            label={`Produto`}
            options={productOptions}
            value={selectedProduct}
            change={setSelectedProduct}
            isMulti={isMulti === true}
            setSearch={value => setSearch(value)}
            filterOption={true}
        />
    );
}

export default SelectProduct;